/**
 * The customer's payment page: /pay/:token
 *
 * Deliberately plain and fast — workshop logo, amount in large type, one
 * button. It has to load on a weak forecourt connection and look like it
 * belongs to the workshop, because a page that looks unfamiliar is a page
 * people do not pay on. No Driveline chrome, no site header.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Loader2, CheckCircle2, ShieldCheck, AlertCircle } from 'lucide-react';
import { API } from '../lib/api';

const inr = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PayInvoice = () => {
  const { token } = useParams();
  const [params] = useSearchParams();
  const justReturned = params.get('paid') === '1';

  const [data, setData] = useState(null);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [err, setErr] = useState('');
  const [fatal, setFatal] = useState('');

  const load = useCallback(async (checkStatus) => {
    try {
      // Coming back from the gateway (or simply reopening) re-checks with the
      // provider, so a webhook that never arrived cannot strand a paid
      // invoice as unpaid.
      const url = checkStatus ? `${API}/public/pay/${token}/status` : `${API}/public/pay/${token}`;
      const { data: d } = await axios.get(url);
      setData(d);
      setAmount(d.balance > 0 ? String(d.balance) : '');
    } catch (e) {
      setFatal(e?.response?.data?.detail || 'This payment link is not valid.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(justReturned); }, [load, justReturned]);

  const pay = async () => {
    setPaying(true); setErr('');
    try {
      const { data: d } = await axios.post(`${API}/public/pay/${token}/checkout`,
        { amount: Number(amount) });
      window.location.href = d.checkout_url;
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not start the payment. Please try again.');
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="animate-spin text-slate-400" size={26} />
      </div>
    );
  }

  if (fatal) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 px-6">
        <div className="max-w-sm text-center" data-testid="pay-error">
          <AlertCircle className="mx-auto text-slate-400" size={30} />
          <p className="mt-3 text-slate-700 font-medium">{fatal}</p>
          <p className="mt-1 text-sm text-slate-500">Please ask the workshop for a fresh link.</p>
        </div>
      </div>
    );
  }

  const accent = data.brand_color || '#0d9488';
  const isDeposit = data.kind === 'estimate';
  const settled = data.balance <= 0;
  const max = Number(data.balance || 0);
  const entered = Number(amount || 0);
  const invalid = entered < 1 || entered > max + 0.005;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8" data-testid="pay-page">
      <div className="max-w-md mx-auto">
        {/* Workshop masthead — their mark, not ours. */}
        <div className="text-center mb-6">
          {data.has_logo && data.workshop_slug ? (
            <img
              src={`${API}/public/workshop/${data.workshop_slug}/logo`}
              alt={data.workshop_name}
              className="h-14 mx-auto object-contain"
              data-testid="pay-workshop-logo"
            />
          ) : (
            <div className="text-lg font-semibold" style={{ color: accent }} data-testid="pay-workshop-name">
              {data.workshop_name}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100">
            <div className="text-xs uppercase tracking-wider text-slate-400">
              {isDeposit ? 'Deposit for estimate' : 'Invoice'}
            </div>
            <div className="text-base font-semibold text-slate-900" data-testid="pay-invoice-number">
              {data.invoice_number}
            </div>
            {data.vehicle && <div className="text-sm text-slate-500 mt-0.5">{data.vehicle}</div>}
            {data.subject && <div className="text-sm text-slate-500">{data.subject}</div>}
          </div>

          <div className="px-6 py-6">
            {settled ? (
              <div className="text-center py-4" data-testid="pay-settled">
                <CheckCircle2 className="mx-auto" size={36} style={{ color: accent }} />
                <div className="mt-3 text-lg font-semibold text-slate-900">
                  {isDeposit ? 'Deposit received' : 'Paid in full'}
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  {isDeposit
                    ? <>₹ {inr(data.paid)} received. Your job is confirmed — this will be adjusted against the final invoice.</>
                    : <>₹ {inr(data.paid)} received against ₹ {inr(data.total)}. Thank you.</>}
                </div>
              </div>
            ) : (
              <>
                <div className="text-xs uppercase tracking-wider text-slate-400">
                  {isDeposit ? 'Deposit requested' : 'Amount due'}
                </div>
                <div className="text-4xl font-semibold tracking-tight text-slate-900 mt-1" data-testid="pay-balance">
                  ₹ {inr(data.balance)}
                </div>
                <div className="text-xs text-slate-500 mt-1.5">
                  {isDeposit ? <>Estimate total ₹ {inr(data.total)}</> : <>Invoice total ₹ {inr(data.total)}</>}
                  {data.paid > 0 && <> · already received ₹ {inr(data.paid)}</>}
                </div>
                {isDeposit && (
                  <div className="text-[11px] text-slate-500 mt-2" data-testid="pay-deposit-note">
                    This deposit confirms the job and is adjusted against your final invoice.
                  </div>
                )}

                {data.accepts_online ? (
                  <>
                    <div className="mt-6">
                      <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">
                        Paying now
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400 text-lg">₹</span>
                        <input
                          type="number"
                          inputMode="decimal"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          className="flex-1 border border-slate-300 rounded-lg px-3 py-2.5 text-lg font-semibold text-slate-900 focus:outline-none focus:ring-2"
                          style={{ '--tw-ring-color': accent }}
                          data-testid="pay-amount-input"
                        />
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1.5">
                        {entered > max + 0.005
                          ? <span className="text-amber-700">Maximum ₹ {inr(max)} — that is all that is outstanding.</span>
                          : isDeposit
                            ? 'Pay the full deposit, or a part of it.'
                            : 'Pay the full balance, or enter a smaller amount as an advance.'}
                      </div>
                    </div>

                    {err && (
                      <div className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="pay-checkout-error">
                        {err}
                      </div>
                    )}

                    <button
                      onClick={pay}
                      disabled={paying || invalid}
                      className="mt-5 w-full text-white text-base font-semibold py-3.5 rounded-xl disabled:opacity-50 transition-opacity"
                      style={{ backgroundColor: accent }}
                      data-testid="pay-now-btn"
                    >
                      {paying
                        ? <span className="inline-flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Opening secure checkout…</span>
                        : `Pay ₹ ${inr(amount || 0)}`}
                    </button>
                    <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
                      <ShieldCheck size={13} />
                      Card, UPI, netbanking or wallet · paid directly to {data.workshop_name}
                    </div>
                  </>
                ) : data.upi_vpa ? (
                  <div className="mt-6 text-center" data-testid="pay-upi-block">
                    <div className="inline-block p-3 bg-white border border-slate-200 rounded-xl">
                      <QRCodeSVG value={data.upi_uri || ''} size={176} level="M" />
                    </div>
                    <div className="mt-3 text-sm text-slate-600">
                      Scan with any UPI app to pay <b>₹ {inr(data.balance)}</b>
                    </div>
                    <div className="mt-1 font-mono text-sm" style={{ color: accent }} data-testid="pay-upi-vpa">
                      {data.upi_vpa}
                    </div>
                    <div className="mt-3 text-[11px] text-slate-500">
                      Once paid, please send the UPI reference (UTR) to the workshop so they
                      can mark your invoice settled.
                    </div>
                  </div>
                ) : (
                  <div className="mt-6 text-sm text-slate-500" data-testid="pay-unavailable">
                    Online payment is not set up for this workshop. Please contact them
                    {data.workshop_phone ? ` on ${data.workshop_phone}` : ''} to settle this invoice.
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="text-center mt-5 text-[11px] text-slate-400">
          Billed by {data.workshop_name}
          {data.workshop_phone ? ` · ${data.workshop_phone}` : ''}
        </div>
      </div>
    </div>
  );
};

export default PayInvoice;
